import React, { useState, useEffect } from "react";
import { useUiContextStore } from "../../stores/uiContextStore";
import { useParams, useNavigate } from "react-router";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Loader,
  Edit2,
  MoreVertical,
  Search,
} from "lucide-react";
import {
  getAsset,
  CMDBAsset,
  listComponentMappings,
  CMDBComponentMapping as CMDBComponentMappingType,
  createComponentMapping,
  updateComponentMapping,
  deleteComponentMapping,
  listCMDBComponents,
  getCMDBApplication,
  CMDBApplication,
  unlinkAssetFromApplication,
  deleteAsset,
  updateAsset,
} from "../../api/frameworksApi";
import { useToastStore } from "../../stores/toastStore";
import { RBACAction, RBACMultiAction } from "../components/RBACButton";
import { DataTable, Column } from "../components/DataTable";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  ModalFooterButtons,
} from "../components/Modal";
import { Tip } from "../components/ui/tooltip";

export function CMDBApplicationAssetDetail() {
  const { applicationId, assetId } = useParams<{
    applicationId: string;
    assetId: string;
  }>();
  const navigate = useNavigate();

  const [asset, setAsset] = useState<CMDBAsset | null>(null);
  const [application, setApplication] = useState<CMDBApplication | null>(null);
  // Publish the open asset with the application it belongs to, so cmdb.yaml's {item} and {parent}
  // questions both apply. Cleared on unmount.
  useEffect(() => {
    const name = asset?.name;
    useUiContextStore
      .getState()
      .setSelectedItem(
        assetId && name
          ? { id: assetId, name, parentName: application?.name }
          : null,
      );
    return () => useUiContextStore.getState().clearSelectedItem();
  }, [assetId, asset, application]);
  const [components, setComponents] = useState<CMDBComponentMappingType[]>([]);
  const [availableComponents, setAvailableComponents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [compsLoading, setCompsLoading] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Component Mapping modal state
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [editingComponentId, setEditingComponentId] = useState<string | null>(
    null,
  );
  const [componentSearchQuery, setComponentSearchQuery] = useState("");
  const [compTableSearch, setCompTableSearch] = useState("");
  const [componentForm, setComponentForm] = useState({
    componentId: "",
    port: "",
    protocol: "TCP" as "TCP" | "UDP",
    status: "RUNNING" as "RUNNING" | "STOPPED" | "UNKNOWN",
  });

  // Edit Asset modal state
  const [showEditAssetModal, setShowEditAssetModal] = useState(false);
  const [editAssetForm, setEditAssetForm] = useState({
    name: "",
    description: "",
    status: "ACTIVE" as "ACTIVE" | "INACTIVE" | "DECOMMISSIONED",
  });

  // Per-form in-flight flags (block double-submit)
  const [submittingComponent, setSubmittingComponent] = useState(false);
  const [submittingAsset, setSubmittingAsset] = useState(false);

  useEffect(() => {
    if (applicationId && assetId) {
      loadData();
    }
  }, [applicationId, assetId]);

  useEffect(() => {
    if (asset) {
      loadComponents();
    }
  }, [asset]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [appRes, assetRes] = await Promise.all([
        getCMDBApplication(applicationId!),
        getAsset(assetId!),
      ]);
      setApplication(appRes.data || null);
      setAsset(assetRes.data || null);
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to load data",
        );
      navigate("/cmdb/applications");
    } finally {
      setLoading(false);
    }
  };

  const loadComponents = async () => {
    setCompsLoading(true);
    try {
      const res = await listComponentMappings(assetId!);
      setComponents(res.data || []);
    } catch (err) {
      console.error("Failed to load components");
    } finally {
      setCompsLoading(false);
    }
  };

  const loadAvailableComponents = async () => {
    try {
      const res = await listCMDBComponents({
        q: componentSearchQuery,
      });
      setAvailableComponents(res.data || []);
    } catch (err) {
      console.error("Failed to load components");
    }
  };

  const handleCreateComponent = async () => {
    if (submittingComponent) return;
    if (!componentForm.componentId || !componentForm.port) {
      useToastStore
        .getState()
        .showError("Error", "Component and Port are required");
      return;
    }
    const portNum = Number.parseInt(componentForm.port, 10);
    if (!Number.isFinite(portNum) || portNum < 1 || portNum > 65535) {
      useToastStore
        .getState()
        .showError("Error", "Port must be a number between 1 and 65535");
      return;
    }

    setSubmittingComponent(true);
    try {
      await createComponentMapping(assetId!, {
        componentId: componentForm.componentId,
        port: portNum,
        protocol: componentForm.protocol,
        status: componentForm.status,
      });
      useToastStore
        .getState()
        .showSuccess("Success", "Component mapped successfully");
      setShowComponentModal(false);
      setComponentForm({
        componentId: "",
        port: "",
        protocol: "TCP",
        status: "RUNNING",
      });
      loadComponents();
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to map component",
        );
    } finally {
      setSubmittingComponent(false);
    }
  };

  const handleEditComponent = async () => {
    if (submittingComponent) return;
    if (
      !editingComponentId ||
      !componentForm.componentId ||
      !componentForm.port
    ) {
      useToastStore
        .getState()
        .showError("Error", "Component and Port are required");
      return;
    }
    const portNum = Number.parseInt(componentForm.port, 10);
    if (!Number.isFinite(portNum) || portNum < 1 || portNum > 65535) {
      useToastStore
        .getState()
        .showError("Error", "Port must be a number between 1 and 65535");
      return;
    }

    setSubmittingComponent(true);
    try {
      await updateComponentMapping(editingComponentId, {
        port: portNum,
        protocol: componentForm.protocol,
        status: componentForm.status,
      });
      useToastStore
        .getState()
        .showSuccess("Success", "Component mapping updated successfully");
      setShowComponentModal(false);
      setEditingComponentId(null);
      setComponentForm({
        componentId: "",
        port: "",
        protocol: "TCP",
        status: "RUNNING",
      });
      loadComponents();
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error
            ? err.message
            : "Failed to update component mapping",
        );
    } finally {
      setSubmittingComponent(false);
    }
  };

  const handleDeleteComponent = async (
    mappingId: string,
    componentTitle: string,
  ) => {
    if (!window.confirm(`Remove ${componentTitle} from this asset?`)) return;

    try {
      await deleteComponentMapping(mappingId);
      useToastStore
        .getState()
        .showSuccess("Success", "Component mapping deleted successfully");
      loadComponents();
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error
            ? err.message
            : "Failed to delete component mapping",
        );
    }
  };

  const startEditComponent = (mapping: CMDBComponentMappingType) => {
    setComponentForm({
      componentId: mapping.componentId,
      port: mapping.port?.toString() || "",
      protocol: mapping.protocol === "UDP" ? "UDP" : "TCP",
      status: mapping.status,
    });
    setEditingComponentId(mapping.id);
    loadAvailableComponents();
    setShowComponentModal(true);
  };

  const handleEditAsset = async () => {
    if (submittingAsset) return;
    if (!editAssetForm.name.trim()) {
      useToastStore.getState().showError("Error", "Asset name is required");
      return;
    }

    setSubmittingAsset(true);
    try {
      await updateAsset(assetId!, {
        name: editAssetForm.name,
        description: editAssetForm.description,
        status: editAssetForm.status,
      });
      useToastStore
        .getState()
        .showSuccess("Success", "Asset updated successfully");
      setShowEditAssetModal(false);
      loadData();
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to update asset",
        );
    } finally {
      setSubmittingAsset(false);
    }
  };

  const startEditAsset = () => {
    if (!asset) return;
    setEditAssetForm({
      name: asset.name,
      description: asset.description || "",
      status: asset.status,
    });
    setShowEditAssetModal(true);
  };

  const handleUnlinkAsset = async () => {
    if (!window.confirm(`Remove this asset from ${application?.name}?`)) return;

    try {
      if (!asset?.applications || asset.applications.length === 0) {
        useToastStore.getState().showError("Error", "Mapping not found");
        return;
      }

      const mapping = asset.applications.find(
        (app: any) => app.applicationId === applicationId,
      );
      if (!mapping) {
        useToastStore.getState().showError("Error", "Mapping not found");
        return;
      }

      await unlinkAssetFromApplication(mapping.id);
      useToastStore
        .getState()
        .showSuccess("Success", "Asset unmapped successfully");
      navigate(`/cmdb/applications/${applicationId}`);
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to unmap asset",
        );
    }
  };

  const handleDeleteAsset = async () => {
    if (!window.confirm(`Are you sure you want to delete "${asset?.name}"?`))
      return;

    try {
      await deleteAsset(assetId!);
      useToastStore
        .getState()
        .showSuccess("Success", "Asset deleted successfully");
      navigate(`/cmdb/applications/${applicationId}`);
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to delete asset",
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="size-8 d animate-spin" />
      </div>
    );
  }

  if (!asset || !application) {
    return (
      <div className="p-6 space-y-4">
        <button
          onClick={() => navigate(`/cmdb/applications/${applicationId}`)}
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
        >
          <ArrowLeft className="size-4" />
          Back to Application
        </button>
        <div className="rounded-lg border border-border bg-muted/50 p-6 text-center">
          <p className="d">Asset not found</p>
        </div>
      </div>
    );
  }

  const compColumns: Column<CMDBComponentMappingType>[] = [
    {
      key: "component.title",
      header: "Component",
      align: "left",
      render: (row) => (
        <span className="font-medium">{row.component.title}</span>
      ),
    },
    {
      key: "component.componentType",
      header: "Type",
      align: "left",
      render: (row) => (
        <span className="d text-sm">{row.component.componentType || "-"}</span>
      ),
    },
    {
      key: "port",
      header: "Port",
      align: "center",
      render: (row) => <span className="d text-sm">{row.port || "-"}</span>,
    },
    {
      key: "protocol",
      header: "Protocol",
      align: "center",
      render: (row) => <span className="d text-sm">{row.protocol}</span>,
    },
    {
      key: "status",
      header: "Status",
      align: "center",
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-md text-xs font-medium ${
            row.status === "RUNNING"
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
              : row.status === "STOPPED"
                ? "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400"
                : "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400"
          }`}
        >
          {row.status}
        </span>
      ),
    },
    {
      key: "_actions",
      header: "Actions",
      align: "center",
      width: "w-20",
      render: (row) => (
        <div className="flex items-center justify-center gap-2">
          <Tip label="Edit Component" side="bottom">
            <button
              onClick={() => startEditComponent(row)}
              className="p-1.5 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 rounded transition-colors"
            >
              <Edit2 className="size-4 d hover:text-indigo-600" />
            </button>
          </Tip>
          <RBACAction module="cmdb" action="delete">
            <Tip label="Delete Component" side="bottom">
              <button
                onClick={() => handleDeleteComponent(row.id, row.component.title)}
                className="p-1.5 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors"
              >
                <Trash2 className="size-4 d hover:text-red-600" />
              </button>
            </Tip>
          </RBACAction>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Breadcrumb */}
      <button
        onClick={() => navigate(`/cmdb/applications/${applicationId}`)}
        className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
      >
        <ArrowLeft className="size-4" />
        Back to {application.name}
      </button>

      {/* Asset Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{asset.name}</h1>
          <div className="flex items-center gap-4 mt-2 text-sm d">
            <span>Type: {asset.assetType}</span>
            {asset.ipAddress && <span>IP: {asset.ipAddress}</span>}
            {asset.accountId && <span>Account: {asset.accountId}</span>}
            <span
              className={`px-2 py-1 rounded-md text-xs font-medium ${
                asset.status === "ACTIVE"
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                  : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400"
              }`}
            >
              {asset.status}
            </span>
          </div>
        </div>

        {/* Actions Menu */}
        <div className="relative">
          <button
            onClick={() => setShowActionsMenu(!showActionsMenu)}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <MoreVertical className="size-5 d" />
          </button>

          {showActionsMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-lg shadow-lg z-10">
              <button
                onClick={() => {
                  startEditAsset();
                  setShowActionsMenu(false);
                }}
                className="w-full text-left px-4 py-2 hover:bg-muted transition-colors text-sm flex items-center gap-2"
              >
                <Edit2 className="size-4" />
                Edit Asset
              </button>
              <button
                onClick={() => {
                  navigate(`/cmdb/assets`);
                  setShowActionsMenu(false);
                }}
                className="w-full text-left px-4 py-2 hover:bg-muted transition-colors text-sm"
              >
                View All Assets
              </button>
              <button
                onClick={() => {
                  handleUnlinkAsset();
                  setShowActionsMenu(false);
                }}
                className="w-full text-left px-4 py-2 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors text-sm text-red-600"
              >
                Unlink from This App
              </button>
              <button
                onClick={() => {
                  handleDeleteAsset();
                  setShowActionsMenu(false);
                }}
                className="w-full text-left px-4 py-2 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors text-sm text-red-600 border-t border-border"
              >
                Delete Asset
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Components Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Components</h2>
          <RBACMultiAction
            checks={[
              ["cmdb", "create"],
              ["cmdb", "update"],
            ]}
            type="any"
          >
            <button
              onClick={() => {
                loadAvailableComponents();
                setShowComponentModal(true);
                setEditingComponentId(null);
                setComponentForm({
                  componentId: "",
                  port: "",
                  protocol: "TCP",
                  status: "RUNNING",
                });
              }}
              className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
            >
              <Plus className="size-4" />
              Add Component
            </button>
          </RBACMultiAction>
        </div>

        {components.length > 0 && (
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d" />
            <input
              type="text"
              placeholder="Search components…"
              value={compTableSearch}
              onChange={(e) => setCompTableSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm"
            />
          </div>
        )}

        <DataTable
          columns={compColumns}
          data={
            compTableSearch
              ? components.filter(
                  (c) =>
                    c.component.title
                      .toLowerCase()
                      .includes(compTableSearch.toLowerCase()) ||
                    (c.component.componentType || "")
                      .toLowerCase()
                      .includes(compTableSearch.toLowerCase()),
                )
              : components
          }
          loading={compsLoading}
          emptyText="No components mapped"
        />
      </div>

      {/* Component Mapping Modal */}
      <Modal
        open={showComponentModal}
        onClose={() => {
          setShowComponentModal(false);
          setEditingComponentId(null);
          setComponentForm({
            componentId: "",
            port: "",
            protocol: "TCP",
            status: "RUNNING",
          });
        }}
        title={editingComponentId ? "Edit Component Mapping" : "Add Component"}
        size="sm"
        footer={
          <ModalFooterButtons
            onCancel={() => {
              setShowComponentModal(false);
              setEditingComponentId(null);
            }}
            onSubmit={
              editingComponentId ? handleEditComponent : handleCreateComponent
            }
            submitLabel="Save"
            submitDisabled={submittingComponent}
          />
        }
      >
        <div className="space-y-4">
          <FormField label="Component" required>
            <FormSelect
              value={componentForm.componentId}
              onChange={(value) =>
                setComponentForm({ ...componentForm, componentId: value })
              }
              options={[
                { value: "", label: "Select a component" },
                ...availableComponents.map((comp) => ({
                  value: comp.id,
                  label: comp.title,
                })),
              ]}
            />
          </FormField>

          <FormField label="Port" required>
            <FormInput
              value={componentForm.port}
              onChange={(value) =>
                setComponentForm({ ...componentForm, port: value })
              }
              placeholder="e.g. 8080"
              type="number"
            />
          </FormField>

          <FormField label="Protocol" required>
            <FormSelect
              value={componentForm.protocol}
              onChange={(value) =>
                setComponentForm({
                  ...componentForm,
                  protocol: value as "TCP" | "UDP",
                })
              }
              options={[
                { value: "TCP", label: "TCP" },
                { value: "UDP", label: "UDP" },
              ]}
            />
          </FormField>

          <FormField label="Status" required>
            <FormSelect
              value={componentForm.status}
              onChange={(value) =>
                setComponentForm({
                  ...componentForm,
                  status: value as "RUNNING" | "STOPPED" | "UNKNOWN",
                })
              }
              options={[
                { value: "RUNNING", label: "Running" },
                { value: "STOPPED", label: "Stopped" },
                { value: "UNKNOWN", label: "Unknown" },
              ]}
            />
          </FormField>
        </div>
      </Modal>

      {/* Edit Asset Modal */}
      <Modal
        open={showEditAssetModal}
        onClose={() => setShowEditAssetModal(false)}
        title="Edit Asset"
        size="sm"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowEditAssetModal(false)}
            onSubmit={handleEditAsset}
            submitLabel="Save"
            submitDisabled={submittingAsset}
          />
        }
      >
        <div className="space-y-4">
          <FormField label="Name" required>
            <FormInput
              value={editAssetForm.name}
              onChange={(value) =>
                setEditAssetForm({ ...editAssetForm, name: value })
              }
              placeholder="Asset name"
            />
          </FormField>

          <FormField label="Status" required>
            <FormSelect
              value={editAssetForm.status}
              onChange={(value) =>
                setEditAssetForm({
                  ...editAssetForm,
                  status: value as "ACTIVE" | "INACTIVE" | "DECOMMISSIONED",
                })
              }
              options={[
                { value: "ACTIVE", label: "Active" },
                { value: "INACTIVE", label: "Inactive" },
                { value: "DECOMMISSIONED", label: "Decommissioned" },
              ]}
            />
          </FormField>

          <FormField label="Description">
            <FormInput
              value={editAssetForm.description}
              onChange={(value) =>
                setEditAssetForm({ ...editAssetForm, description: value })
              }
              placeholder="Asset description"
            />
          </FormField>
        </div>
      </Modal>
    </div>
  );
}
